import { useDispatch, useSelector } from "react-redux";

import {
    setAllQuestionsError,
    setCurrentQuestionId,
    setGroupCompleted,
    handleHideOrShowQuestion,
    setQuestionAnswer,
    setShowInlineError,
    resetAnswerOfCurrentQuestion
} from "../../../features/editAssessment/editAssessmentSlice";
import CONSTANT from "../../../constants/Constant";
import {
    checkAnswerIdValidation, getConditionalQuestions,
    handleQuestionAnswerValidation,
    saveSingleQuestionAnswer,
} from "../EditAssessmentService";
import { useDebounce } from "../../../hooks/useDebounce";
import { deepClone, getAssessmentCompletion } from "../../../utils/Utils";
import {
    doesHaveAnswerIdRequired, getObjForUpdatingAnswerAtRedux,
    getClickQuestionSubGroupIndex, isCurrentQuestionAnswered
} from "../EditAssessmentUtils";
import ExamService from "../../../services/ExamService";
import answerValidationService from "../../../services/answerValidation/AnswerValidationService";
import { saveDemoInfoAtLocalStorage } from "../../../services/LocalStorageService";
import useOSWScore from "./useOSWScore";
import { updateCCQuestionAnswerAndSetCompletion } from "../../../features/editAssessment/editAssessmentThunks";

const useEditAssessmentBody = (setSelectedElementForScroll) => {
    const dispatch = useDispatch();
    const { handleUpdateOSWScore } = useOSWScore();
    const editAssessmentGroup = useSelector(state => state.editAssessment);
    const groups = editAssessmentGroup?.groups || [];

    const baseData = editAssessmentGroup?.baseData || {};
    const currentGroupId = baseData?.currentGroupId || CONSTANT.ASSESSMENT_GROUP.DEMOGRAPHICS.key;
    const currentSubGroupIndex = baseData?.currentSubGroupIndex || 0;
    const testId = baseData?.testId || '';
    const showInlineError = baseData?.showInlineError || false;

    const currentGroup = groups.find(group => group.groupId === currentGroupId);

    const debouncedSaveAnswer = useDebounce(async (question, answers) => {
        await handleSaveSingleQuestionAnswer(question, answers);
    }, 500);

    const handleSaveSingleQuestionAnswer = async (question, answers) => {
        const questionId = question?.questionId || '';
        const responseType = question?.responseType || '';
        const answer = answers[0];

        let answerValue;
        if((question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) || (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)) {
            answerValue = answer?.AnswerList.length > 0 ? answer?.AnswerList : [];
        } else {
            answerValue = answer?.AnswerTitle;
        }
        const validationResult = answerValidationService.validate(questionId, responseType, answerValue);
        // As for checkbox anyone can unselect all option
        if(
            !(
                (question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) ||
                (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)
            ) &&
            !validationResult.valid
        ) {
            return;
        }

        if(currentGroupId === CONSTANT.ASSESSMENT_GROUP.DEMOGRAPHICS.key) {
            const answer = answers[0];
            const answerTitle = answer?.AnswerTitle;

            saveDemoInfoAtLocalStorage(questionId, { AnswerValue : answerTitle });
        }

        const response = await saveSingleQuestionAnswer(testId, answers);
        if(response?.success) {
            handleIsGroupCompleted(response)
        }
    }

    const handleIsGroupCompleted = (response) => {
        const data = response?.data;
        if(!data) {
            console.error("No Data Found!");
            return;
        }

        const mapOfAssessmentCompletion = getAssessmentCompletion(data);
        mapOfAssessmentCompletion.forEach(group => {
            if(group?.groupCompleted && group.key !== CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.key) {
                dispatch(setGroupCompleted({ groupId: group?.key, isCompleted: true }));
            }
        });
    }

    const handleAnswerSave  = async (value) => {
        if(!checkAnswerIdValidation(value?.question, value?.answerId)) {
            return;
        }

        const responseId = value?.answerId;
        const responseValue = value?.answerText;
        const question = value?.question;
        const questionId = question?.questionId;

        // Update the answer in Redux. The slice will automatically recalculate the blocked state.
        dispatch(setQuestionAnswer({ 
            questionId, 
            answerId: responseId, 
            answerValue: responseValue, 
            subGroupIndex: currentSubGroupIndex 
        }));

        let tempSubGroupIndex = currentSubGroupIndex;
        if(currentGroupId === CONSTANT.ASSESSMENT_GROUP.OUTCOME_ASSESSMENT.key) {
            tempSubGroupIndex = getClickQuestionSubGroupIndex(currentGroup, questionId);
        }

        const answerObjForSetQuestionAnswer = {
            questionId: questionId,
            answerValue: responseValue,
            answerId: responseId
        };

        // We use the groups from state to find the updated question/subgroup
        const currentGroupInState = groups.find(g => g.groupId === currentGroupId);
        let questionsOfSelectedSubGroup = currentGroupInState?.subGroups[tempSubGroupIndex]?.questions || [];
        
        // Simulate update to local visibility and validation logic if needed
        let updatedQuestions = updateCurrentQuestionAnswer(questionsOfSelectedSubGroup, answerObjForSetQuestionAnswer);
        
        if(showInlineError) {
            handleQuestionAnswerValidation(updatedQuestions, dispatch, setAllQuestionsError, setShowInlineError, currentGroupId, tempSubGroupIndex);
        }
        
        focusNextQuestion(questionId, tempSubGroupIndex);
        handleUpdateOSWScore(updatedQuestions, tempSubGroupIndex);
        
        let answeredQuestion = updatedQuestions.find(q => q.questionId === questionId);
        await handleChildQuestion(updatedQuestions, answeredQuestion);
        await handleDependsOnQuestion(updatedQuestions, answeredQuestion);
        
        const answers = getAnswersObjForSaving(answeredQuestion, responseId, responseValue);
        if(doesHaveAnswerIdRequired(value?.question)) {
            await handleSaveSingleQuestionAnswer(question, answers);
        } else {
            debouncedSaveAnswer(question, answers);
        }
    }

    const handleDependsOnQuestion = async (questions, question) => {
        let allowedResponseTypes = [CONSTANT.ELEMENT_TYPES.DROPDOWN, CONSTANT.ELEMENT_TYPES.BUTTON];
        if(!allowedResponseTypes.includes(question?.responseType)) return;


        const allQuestionsWhichDependsOnCurrentQuestion = questions.filter(qus => qus.dependsOnQuestionId === question.questionId);
        if(allQuestionsWhichDependsOnCurrentQuestion.length === 0) return;

        allQuestionsWhichDependsOnCurrentQuestion.forEach(qus => {
            dispatch(resetAnswerOfCurrentQuestion({ questionId: qus.questionId }));
            ExamService.clearAssessments({ TestId: testId, QuestionIds: [qus.questionId] });
        });
    }

    const updateCurrentQuestionAnswer = (questionsOfSelectedSubGroup, answerObjForSetQuestionAnswer) => {
        const tempUpdatedQuestions = deepClone(questionsOfSelectedSubGroup);

        tempUpdatedQuestions.forEach(question => {
            if(question.questionId === answerObjForSetQuestionAnswer.questionId) {
                if((question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) || (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)) {
                    let tempAnswerList = question?.answerList ?? [];
                    let doesAlreadySelectedCurrentAnswer = tempAnswerList.find(answer => answer === answerObjForSetQuestionAnswer.answerId);
                    if(doesAlreadySelectedCurrentAnswer) {
                        tempAnswerList = tempAnswerList.filter(currentAnswerId => currentAnswerId !== answerObjForSetQuestionAnswer.answerId);
                    } else {
                        tempAnswerList.push(answerObjForSetQuestionAnswer.answerId);
                    }

                    question.answerList = tempAnswerList;
                } else {
                    question.answerValue = answerObjForSetQuestionAnswer.answerValue;
                    question.answerId = answerObjForSetQuestionAnswer.answerId;
                }

            }
        });

        return tempUpdatedQuestions;
    }

    const getAnswersObjForSaving = (question, responseId, responseValue) => {
        let answers;
        if((question?.responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX) || (question?.responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT)) {
            let tempAnswerList = question?.answerList ?? [];
            answers = [
                {
                    AnswerList: tempAnswerList,
                    QuestionId: question?.questionId ?? 0
                }
            ]
        } else {
            answers = [
                {
                    GroupId: currentGroupId,
                    AnswerId: responseId,
                    AnswerTitle: responseValue,
                    QuestionId: question?.questionId ?? 0
                }
            ]
        }

        return answers;
    }

    const handleChildQuestion = async (questions, question) => {
        if(!question?.answerId) return;

        let { childQuestionIdsOfSelectedResponse, childQuestionIdsOfNotSelectedResponse } =
            getConditionalQuestions(question);

        // remove questions ids those are in both arrays as do not need to hide question those are inside selected question
        childQuestionIdsOfNotSelectedResponse = childQuestionIdsOfNotSelectedResponse
            .filter((questionId) => !childQuestionIdsOfSelectedResponse.includes(questionId));

        updateVisibility(childQuestionIdsOfNotSelectedResponse, true);
        updateVisibility(childQuestionIdsOfSelectedResponse, false);

        if(childQuestionIdsOfNotSelectedResponse.length > 0) {
            await ExamService.clearAssessments({ TestId: testId, QuestionIds: childQuestionIdsOfNotSelectedResponse });
        }
    }

    const updateVisibility = (questionIds, isDefaultHide) => {
        questionIds.forEach(questionId => {
            dispatch(handleHideOrShowQuestion({
                questionId,
                isDefaultHide
            }))
        });
    };

    const updateAnswerOnRedux = (questionId, responseId, responseValue, tempSubGroupIndex) => {
        const answerObjForSetQuestionAnswer = getObjForUpdatingAnswerAtRedux(questionId, responseId, responseValue, tempSubGroupIndex);

        dispatch(setQuestionAnswer(answerObjForSetQuestionAnswer));
        return answerObjForSetQuestionAnswer;
    }

    const focusNextQuestion = (currentQuestionId, subGroupIndex) => {
        const subGroups = currentGroup?.subGroups || [];
        const tempQuestions = deepClone(subGroups[subGroupIndex]?.questions || []);

        const currentQuestionIndex = tempQuestions.findIndex(question => question.questionId === currentQuestionId);
        let nextQuestionIndex = currentQuestionIndex + 1;
        while((nextQuestionIndex < tempQuestions.length) && (tempQuestions[nextQuestionIndex].isDefaultHide)) nextQuestionIndex++;

        if(nextQuestionIndex < tempQuestions.length) {
            let nextQuestionHTMLElement = document.getElementById(`cc-question-${currentQuestionId}`);
            if(nextQuestionHTMLElement) {
                setSelectedElementForScroll && setSelectedElementForScroll(nextQuestionHTMLElement);
                dispatch(setCurrentQuestionId(tempQuestions[nextQuestionIndex].questionId));
            }
        }
    }

    const handleUpdateCCQuestionAnswer = (answerId, answerValue) => {
        dispatch(updateCCQuestionAnswerAndSetCompletion(answerId, answerValue));
    }

    const getDependQuestionOfCurrentQuestion = (question) => {
        const currentSubGroup = currentGroup?.subGroups[currentSubGroupIndex];
        // console.log("currentSubGroup", currentSubGroup);
        if(!currentSubGroup) return {};


        const subGroupQuestions = currentSubGroup?.questions || [];
        // console.log("subGroupQuestions", subGroupQuestions);
        if(subGroupQuestions.length === 0) return {};

        // console.log('dependsOnQuestionId', question.dependsOnQuestionId);
        const dependsOnQuestion = subGroupQuestions.find(qus => qus.questionId === question.dependsOnQuestionId);
        // console.log('dependsOnQuestion', dependsOnQuestion);
        if(!dependsOnQuestion) return {};

        return dependsOnQuestion;
    }

    const willShowCurrentQuestion = (question) => {
        if(!question) return false;

        if(question?.isDefaultHide) return false;
        // else if(question?.dependsOnQuestionId) {
        //     let dependsOnQuestion = getDependQuestionOfCurrentQuestion(question);
        //     // console.log('dependsOnQuestion', dependsOnQuestion);
        //     if(Object.keys(dependsOnQuestion).length === 0) return false;
        //
        //     const isCurrentQuestionHaveAnswer = isCurrentQuestionAnswered(dependsOnQuestion)
        //     // console.log("isCurrentQuestionHaveAnswer", isCurrentQuestionHaveAnswer);
        //     if(isCurrentQuestionHaveAnswer) {
        //         return true;
        //     }
        // }

        return true;
        // else {
        //     return question?.isDefaultHide ? false : true;
        // }
    }


    const getDropDownOptions = (question) => {
        if(question?.dependsOnQuestionId) {
            // console.log(question.responses)

            let dependsOnQuestion = getDependQuestionOfCurrentQuestion(question);
            if(Object.keys(dependsOnQuestion).length === 0) return [];

            // console.log('dependsOnQuestion', dependsOnQuestion);
            let answerIdOfDependsOnQuestion = dependsOnQuestion?.answerId;

            let response = question.responses.filter(res => res.dependsOnAnswerId === answerIdOfDependsOnQuestion);
            // console.log('response', response);

            return response;
        } else {
            return question.responses;
        }
    }


    return {
        handleAnswerSave,
        handleUpdateCCQuestionAnswer,
        updateAnswerOnRedux,
        willShowCurrentQuestion,
        getDropDownOptions
    }
}

export default useEditAssessmentBody;
